import { useRef } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { TSTFilterBar, TSTPagination, TSTTable } from "@impero-com/ui";

import { ControlProgramOverviewResource } from "api/generated/model";
import { useControlProgramTagsSuspenseQuery } from "org/tags/controlProgramTags/queries/useControlProgramTagsQuery";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { useEntityProgramListTable } from "./useEntityProgramListTable";

interface EntityProgramsListProps {
    programsWithEntities: ControlProgramOverviewResource[];
    entityId: number;
}

export function EntityProgramsList({
    programsWithEntities,
    entityId,
}: EntityProgramsListProps) {
    const { t } = useTranslation();
    const {
        data: { tags, tagCategories },
    } = useControlProgramTagsSuspenseQuery();
    const { table, getFilterComponent, canAddException, tableId } =
        useEntityProgramListTable(
            programsWithEntities,
            tags,
            tagCategories,
            entityId
        );
    const tableElement = useRef<HTMLDivElement>(null);

    const modeConfig = getFilterModeConfig(t);

    return (
        <div>
            <TSTFilterBar
                table={table}
                tableId={tableId}
                addLabel={t("FilterBar_AddFilter")}
                globalSearchLabel={t("FilterBar_GlobalSearchPlaceholder")}
                getFilterCountLabel={(count) =>
                    t("FilterBar_FiltersApplied", { count })
                }
                rowCountLabel={t("FilterBar_RowCount", {
                    count: table.getPrePaginationRowModel().rows.length,
                })}
                hideFiltersLabel={t("FilterBar_HideFilters")}
                clearAllLabel={t("FilterBar_ClearAll")}
                getFilterComponent={getFilterComponent}
                canAddException={canAddException}
                addExceptionLabel={t("FilterBar_AddException")}
                modeConfig={modeConfig}
            />

            <TSTTable
                tableRef={tableElement}
                table={table}
                noResultsFallback={t("NoResults")}
                noSourceRowsFallback={t("NoResults")}
            />
            <TSTPagination
                table={table}
                pageLabel={t("Pagination_Page")}
                tableElement={tableElement}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
            />
        </div>
    );
}
